import { X } from 'lucide-react-native';
import { useReducedMotion } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { AskConversationProvider, AskUiProvider, useAskUiStoreApi } from './LibraryChatContext';
import { LibraryChatHistory } from './LibraryChatHistory';

import { Drawer, DrawerBackdrop, DrawerContent } from '@/components/ui/drawer';
import { GlassIconButton, GlassSurface, useLiquidGlass } from '@/components/ui/glass';
import { HStack } from '@/components/ui/hstack';
import { View } from '@/components/ui/view';
import type { AskConversationController } from '@/hooks/ask/useAskConversationController';

/** Gap between the floating glass panel and the screen's edges. */
const GLASS_INSET = 8;

/** Replaces the drawer's slide and fade when the reader asks for reduced motion. */
const INSTANT = { type: 'timing', duration: 0 } as const;

/**
 * The reader's conversations, sliding in from the left on compact layouts,
 * where wider ones show them as a column.
 *
 * Where Liquid Glass is drawn it is a floating glass panel, inset from the
 * screen's edges and clear of the status bar and home indicator, over a
 * lighter dim than usual so the thread still reads through the glass.
 * Elsewhere it is an opaque panel flush to the left edge.
 *
 * The drawer renders through the overlay portal at the app root, outside this
 * screen's tree, so it provides the UI store and the conversation again inside
 * the portal; the list reads both.
 *
 * Fill goes on a plain child, not the drawer's own content view, which is
 * animated and does not reliably paint a background on native.
 */
export function HistoryDrawer({
  isOpen,
  onClose,
  controller,
}: {
  isOpen: boolean;
  onClose: () => void;
  controller: AskConversationController;
}) {
  const { top, bottom } = useSafeAreaInsets();

  const store = useAskUiStoreApi();

  const hasGlass = useLiquidGlass();

  const isReducedMotion = useReducedMotion();

  const motion = isReducedMotion ? { transition: INSTANT } : null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg" anchor="left">
      <DrawerBackdrop
        className="web:pointer-events-auto"
        {...(hasGlass ? { animate: { opacity: 0.2 } } : null)}
        {...motion}
      />

      <DrawerContent className="border-0 bg-transparent p-0" {...motion}>
        <View
          className="flex-1"
          style={
            hasGlass
              ? {
                  paddingTop: top + GLASS_INSET,
                  paddingBottom: bottom + GLASS_INSET,
                  paddingLeft: GLASS_INSET,
                }
              : undefined
          }>
          <GlassSurface
            className={twJoin('flex-1', hasGlass && 'rounded-[28px]')}
            fallbackClassName="border-r border-outline-200 bg-background-0"
            style={hasGlass ? undefined : { paddingTop: top, paddingBottom: bottom }}>
            <HStack className="justify-end px-2 pt-2">
              <GlassIconButton
                icon={X}
                onPress={onClose}
                accessibilityLabel="Close your previous conversations"
              />
            </HStack>

            <AskUiProvider value={store}>
              <AskConversationProvider value={controller}>
                <LibraryChatHistory onNavigate={onClose} />
              </AskConversationProvider>
            </AskUiProvider>
          </GlassSurface>
        </View>
      </DrawerContent>
    </Drawer>
  );
}
